import React, { useContext, useState } from 'react';
import { useLocalStorage } from 'usehooks-ts';
import { getGeocode, LatLng } from 'src/scripts/utils/findInStores';
import { getCurrentGeoLocationPosition } from 'src/scripts/utils/location';
import { FIND_IN_STORE_POSTAL_CODE } from '../../../../utils/findInStores';
import { ProductContext } from '../../ProductContext';
import FormInput from 'src/scripts/templates/_shared/ui-components/YogaUI/FormInput';

interface CountryConfig {
  [key: string]: { regex: string; maxLength: number; postalOrZip: string };
}

const countryConfig: CountryConfig = {
  US: { regex: '^\\d{5}$', maxLength: 5, postalOrZip: 'zip' },
  CA: { regex: '^[A-Za-z]\\d[A-Za-z][ ]?\\d[A-Za-z]\\d$', maxLength: 7, postalOrZip: 'postal' },
  UK: {
    regex: '[a-zA-Z]{1,2}[0-9][0-9a-zA-Z]?\\s?[0-9][a-zA-Z]{2}$',
    maxLength: 8,
    postalOrZip: 'postal',
  },
};

const FindInStoreForm = ({
  userCountry,
  setLatLongLocalStorage,
  findInStoreApiError,
  setLastValidZip,
}: {
  userCountry: string;
  setLatLongLocalStorage: (data: string) => void;
  findInStoreApiError: boolean;
  setLastValidZip: (data: string) => void;
}) => {
  const [postalCode, setPostalCodeStorage] = useLocalStorage<string>(FIND_IN_STORE_POSTAL_CODE, '');
  const [resetKey, setResetKey] = useState(0);
  const { setFindInStoreApiError } = useContext(ProductContext);

  const countryConfigEntry = countryConfig[userCountry];
  if (!countryConfigEntry) {
    console.error(`Invalid country code: ${userCountry}`);
    return null;
  }
  const { regex, maxLength, postalOrZip } = countryConfigEntry;
  const label = `Search by ${postalOrZip} code`;
  const inputErrorMessage = `Please enter a valid ${postalOrZip} code.`;

  const onFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const form = event.target as HTMLFormElement;
    const formData = new FormData(form);
    const postal = (formData.get('postalCodeFindInStore')?.toString() || '').replace(/\s+/g, ' ').trim().toUpperCase();

    setPostalCodeStorage(postal);

    try {
      const params = {
        postal_code: postal,
        country_code: userCountry.toLowerCase(),
      };
      setFindInStoreApiError(false);
      if (params.postal_code) {
        const geocodeData: LatLng = await getGeocode(params);
        setLatLongLocalStorage(JSON.stringify(geocodeData));
        setLastValidZip(postal);
      }
    } catch (error) {
      console.error(error);
      setFindInStoreApiError(true);
    }
  };

  const handleReset = () => {
    setResetKey((prevKey: number) => prevKey + 1);
  };

  const getCurrentLocation = async (event: React.FormEvent) => {
    event.preventDefault();
    setPostalCodeStorage('');
    setFindInStoreApiError(false);
    try {
      const data = await getCurrentGeoLocationPosition();
      const coordinates: LatLng = { latitude: data?.latitude, longitude: data?.longitude };
      setLatLongLocalStorage(JSON.stringify(coordinates));
    } catch (err) {
      setLatLongLocalStorage('');
    }
    handleReset();
  };

  return (
    <>
      <div className="find-in-store-form-container d-flex">
        <form className="find-in-store-form d-flex" onSubmit={onFormSubmit}>
          <FormInput
            key={resetKey}
            options={{
              type: 'text',
              id: 'postalCodeFindInStore',
              name: 'postalCodeFindInStore',
              errorText: inputErrorMessage,
              label: label,
              pattern: regex,
              defaultValue: postalCode,
              maxLength: maxLength,
              required: true,
              showErrorOnBlur: false,
            }}
          />
          <button className="button-primary arrow-button" type="submit">
            <span className="alo-chevron-right-icon"></span>
          </button>
        </form>
        <div className="current-location-link d-flex">
          or
          <button className="current-location-link__button" onClick={getCurrentLocation}>
            <span>Use my Current Location</span>
          </button>
        </div>
      </div>
      {findInStoreApiError && (
        <p className="alert-text">That {postalOrZip} code does not exist. Please try a different one.</p>
      )}
    </>
  );
};

export default FindInStoreForm;
